import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  Inbox, Bot, Send, Star, Users, Sparkles, MessageCircle,
  Plus, Pencil, Trash2, Save, X, Wifi, WifiOff, Loader2,
  CheckCircle2, Clock, BarChart3, ArrowLeft,
} from "lucide-react";
import { io } from "socket.io-client";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

const STATUS_OPTIONS = [
  { value: "pendiente",   label: "Pendiente",   color: "bg-amber-100 text-amber-700" },
  { value: "en_atencion", label: "En atención", color: "bg-blue-100 text-blue-700" },
  { value: "resuelto",    label: "Resuelto",    color: "bg-green-100 text-green-700" },
];

const SEGMENTS = [
  { value: "",        label: "Todas",            icon: Inbox },
  { value: "starred",  label: "Marcadas",         icon: Star },
  { value: "ai",       label: "Responde IA",      icon: Bot },
  { value: "human",    label: "Necesita asesor",  icon: Users },
];

// ── Toggle ─────────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none">
      <div
        onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition-colors relative ${value ? "bg-supernova-500" : "bg-gray-200"}`}
      >
        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
      </div>
      {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
    </label>
  );
}

function StatCard({ icon: Icon, label, value, tone = "gray" }) {
  const tones = {
    gray:   "bg-gray-50 text-gray-600",
    amber:  "bg-amber-50 text-amber-700",
    blue:   "bg-blue-50 text-blue-700",
    green:  "bg-green-50 text-green-700",
    violet: "bg-violet-50 text-violet-700",
    red:    "bg-red-50 text-red-700",
  };
  return (
    <div className="bg-white rounded-2xl shadow p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tones[tone]}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-xl font-bold text-gray-800 leading-none">{value ?? "—"}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inbox — lista + hilo
// ─────────────────────────────────────────────────────────────────────────────
// ── Nueva conversación (escribir primero a un número) ─────────────────────────
// Meta solo permite texto libre dentro de una ventana de 24h desde el último
// mensaje del CLIENTE — si el número nunca escribió, el envío falla y se
// muestra el error real de Meta tal cual, para que el admin sepa que ese
// número necesita una plantilla aprobada en vez de texto libre.
function NewConversationModal({ onClose, onStarted }) {
  const [phone, setPhone] = useState("");
  const [contactName, setContactName] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const handleStart = async () => {
    if (!phone.trim()) return toast.error("El número es obligatorio");
    if (!body.trim()) return toast.error("Escribe un mensaje");
    setSending(true);
    try {
      const res = await API.post("/whatsapp/conversations/start", {
        phone: phone.trim(),
        contactName: contactName.trim() || undefined,
        body: body.trim(),
      });
      toast.success("Mensaje enviado");
      onStarted(res.data?.data?.conversation?.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo enviar — revisa si el número necesita una plantilla aprobada");
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between bg-supernova-500">
          <div className="flex items-center gap-2 text-white font-bold">
            <MessageCircle size={18} /> Nueva conversación
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Número (con código de país)</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="573001234567" className={inp} />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Nombre (opcional)</label>
            <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Nombre del contacto" className={inp} />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Mensaje</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className={`${inp} resize-none`} placeholder="Escribe tu mensaje aquí…" />
          </div>
          <button
            onClick={handleStart}
            disabled={sending}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm text-white bg-supernova-500 hover:bg-supernova-600 transition disabled:opacity-60"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function InboxTab({ onChanged }) {
  const [agents,        setAgents]        = useState([]);
  const [conversations, setConversations] = useState([]);
  const [segment,       setSegment]       = useState("");
  const [statusFilter,  setStatusFilter]  = useState("");
  const [loadingList,   setLoadingList]   = useState(true);
  const [selectedId,    setSelectedId]    = useState(null);
  const [thread,        setThread]        = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText,     setReplyText]     = useState("");
  const [sending,       setSending]       = useState(false);
  const [showNewConv,   setShowNewConv]   = useState(false);
  const selectedIdRef  = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = { limit: 50 };
      if (statusFilter) params.status = statusFilter;
      if (segment === "starred") params.starred = true;
      if (segment === "ai" || segment === "human") params.segment = segment;
      const res = await API.get("/whatsapp/conversations", { params });
      setConversations(res.data?.data?.data ?? []);
    } catch { toast.error("Error cargando conversaciones"); }
    finally { setLoadingList(false); }
  }, [statusFilter, segment]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    API.get("/whatsapp/agents").then(res => setAgents(res.data?.data?.agents ?? [])).catch(() => {});
  }, []);

  const openConversation = async (id) => {
    setSelectedId(id);
    setLoadingThread(true);
    try {
      const res = await API.get(`/whatsapp/conversations/${id}/messages`);
      setThread({ conversation: res.data?.data?.conversation, messages: res.data?.data?.messages ?? [] });
      setConversations(prev => prev.map(c => c.id === id ? { ...c, unreadCount: 0 } : c));
    } catch { toast.error("Error cargando la conversación"); }
    finally { setLoadingThread(false); }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  // Socket en vivo
  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;
    const socket = io(SOCKET_URL, { transports: ["websocket"], auth: { token } });
    socket.on("whatsapp:message:new", ({ conversationId, message }) => {
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === conversationId);
        if (idx === -1) { loadConversations(); return prev; }
        const isOpen = selectedIdRef.current === conversationId;
        const updated = {
          ...prev[idx],
          lastMessage:   message.body,
          lastMessageAt: message.createdAt,
          unreadCount:   isOpen ? 0 : (message.direction === "inbound" ? (prev[idx].unreadCount || 0) + 1 : prev[idx].unreadCount),
        };
        return [updated, ...prev.filter((_, i) => i !== idx)];
      });
      setThread(prev => {
        if (!prev || prev.conversation?.id !== conversationId) return prev;
        return { ...prev, messages: [...prev.messages, message] };
      });
      onChanged?.();
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePatch = async (patch) => {
    if (!thread) return;
    try {
      const res = await API.patch(`/whatsapp/conversations/${thread.conversation.id}`, patch);
      const updated = res.data?.data?.conversation;
      setThread(t => ({ ...t, conversation: updated }));
      setConversations(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
      onChanged?.();
    } catch { toast.error("Error al actualizar la conversación"); }
  };

  const toggleStar = async (c, e) => {
    e.stopPropagation();
    try {
      const res = await API.patch(`/whatsapp/conversations/${c.id}`, { starred: !c.starred });
      const updated = res.data?.data?.conversation;
      setConversations(prev => prev.map(x => x.id === c.id ? { ...x, ...updated } : x));
      if (thread?.conversation?.id === c.id) setThread(t => ({ ...t, conversation: updated }));
      onChanged?.();
    } catch { toast.error("Error al marcar"); }
  };

  const handleSend = async () => {
    if (!replyText.trim() || !thread) return;
    setSending(true);
    try {
      const res = await API.post(`/whatsapp/conversations/${thread.conversation.id}/messages`, { body: replyText.trim() });
      const message = res.data?.data?.message;
      setThread(t => ({ ...t, messages: [...t.messages, message] }));
      setConversations(prev => prev.map(c => c.id === thread.conversation.id ? { ...c, lastMessage: message.body, lastMessageAt: message.createdAt } : c));
      setReplyText("");
    } catch (err) { toast.error(err.response?.data?.message || "Error enviando el mensaje"); }
    finally { setSending(false); }
  };

  return (
    // Altura: 70% del viewport en móvil (nunca fija — en pantallas angostas
    // antes el grid pasaba a 1 columna pero el alto seguía fijo en 640px,
    // apretando lista + chat uno encima del otro). A partir de `sm` vuelve al
    // alto fijo original, sin cambio visual en desktop.
    <div className="grid sm:grid-cols-[300px_1fr] gap-4 h-[70vh] sm:h-[640px]">
      {/* Left: filters + list — en móvil se oculta mientras hay un chat abierto */}
      <div className={`border border-gray-100 rounded-2xl overflow-hidden flex-col ${selectedId ? "hidden sm:flex" : "flex"}`}>
        <div className="p-2.5 border-b border-gray-100">
          <button
            onClick={() => setShowNewConv(true)}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition"
          >
            <Plus size={13} /> Nueva conversación
          </button>
        </div>
        <div className="p-2.5 border-b border-gray-100 grid grid-cols-2 gap-1.5">
          {SEGMENTS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.value}
                onClick={() => setSegment(s.value)}
                className={`flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-2 rounded-xl transition ${segment === s.value ? "bg-supernova-500 text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}
              >
                <Icon size={12} /> {s.label}
              </button>
            );
          })}
        </div>
        <div className="px-2.5 pb-2.5">
          <select className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">Cualquier estado</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto border-t border-gray-50">
          {loadingList ? (
            <div className="p-4 text-center text-gray-400 text-sm">Cargando…</div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <Inbox size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-xs">Sin conversaciones</p>
            </div>
          ) : conversations.map(c => {
            const st = STATUS_OPTIONS.find(s => s.value === c.status);
            const unread = c.id === selectedId ? 0 : c.unreadCount;
            const aiHandled = c.aiEnabled && c.agentId && c.agent?.active;
            return (
              <button
                key={c.id}
                onClick={() => openConversation(c.id)}
                className={`w-full text-left px-3 py-3 border-b border-gray-50 transition ${selectedId === c.id ? "bg-supernova-50" : "hover:bg-gray-50"}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-bold text-gray-800 truncate flex items-center gap-1">
                    {c.starred && <Star size={11} className="text-amber-400 fill-amber-400 shrink-0" />}
                    {c.contactName || c.phone}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    {unread > 0 && (
                      <span className="bg-supernova-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{unread}</span>
                    )}
                    <span onClick={(e) => toggleStar(c, e)} className="text-gray-300 hover:text-amber-400 cursor-pointer">
                      <Star size={13} className={c.starred ? "text-amber-400 fill-amber-400" : ""} />
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 truncate mb-1.5">{c.lastMessage || "—"}</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${st?.color}`}>{st?.label}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${aiHandled ? "bg-violet-100 text-violet-700" : "bg-orange-100 text-orange-700"}`}>
                    {aiHandled ? <><Bot size={9} /> IA</> : <><Users size={9} /> Asesor</>}
                  </span>
                  {c.agent?.name && <span className="text-[10px] text-gray-400 truncate">{c.agent.name}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: thread — en móvil solo se muestra cuando hay un chat abierto */}
      <div className={`border border-gray-100 rounded-2xl overflow-hidden flex-col ${selectedId ? "flex" : "hidden sm:flex"}`}>
        {!thread ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Selecciona una conversación</p>
            </div>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-gray-100 flex flex-wrap items-center gap-2 bg-gray-50/50">
              <button onClick={() => setSelectedId(null)} className="sm:hidden text-gray-400 hover:text-gray-700 shrink-0">
                <ArrowLeft size={18} />
              </button>
              <button onClick={(e) => toggleStar(thread.conversation, e)} className="text-gray-300 hover:text-amber-400 shrink-0">
                <Star size={16} className={thread.conversation.starred ? "text-amber-400 fill-amber-400" : ""} />
              </button>
              <div className="min-w-0 mr-auto">
                <p className="text-sm font-bold text-gray-800 truncate">{thread.conversation.contactName || thread.conversation.phone}</p>
                <p className="text-xs text-gray-400">{thread.conversation.phone}</p>
              </div>
              <select
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
                value={thread.conversation.status}
                onChange={e => handlePatch({ status: e.target.value })}
              >
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white max-w-[140px]"
                value={thread.conversation.agentId ?? ""}
                onChange={e => handlePatch({ agentId: e.target.value || null })}
              >
                <option value="">Sin agente IA</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <Toggle value={thread.conversation.aiEnabled} onChange={v => handlePatch({ aiEnabled: v })} label="IA activa" />
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-white">
              {loadingThread ? (
                <div className="text-center text-gray-400 text-sm py-6">Cargando…</div>
              ) : thread.messages.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-6">Sin mensajes todavía</div>
              ) : thread.messages.map(m => (
                <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.direction === "outbound" ? "bg-supernova-500 text-white" : "bg-gray-100 text-gray-800"}`}>
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <div className={`flex items-center gap-1 mt-1 text-[10px] ${m.direction === "outbound" ? "text-white/70" : "text-gray-400"}`}>
                      {m.sentByAgentId && <span className="flex items-center gap-0.5"><Bot size={9} /> IA</span>}
                      <span>{new Date(m.createdAt).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 border-t border-gray-100 flex gap-2 bg-gray-50/50">
              <input
                className={`${inp} flex-1`}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !sending) handleSend(); }}
                placeholder="Escribe un mensaje manual…"
              />
              <button onClick={handleSend} disabled={sending || !replyText.trim()} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-50`}>
                <Send size={14} />
              </button>
            </div>
          </>
        )}
      </div>

      {showNewConv && (
        <NewConversationModal
          onClose={() => setShowNewConv(false)}
          onStarted={(newId) => { loadConversations(); if (newId) openConversation(newId); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agentes IA (multiagente) — CRUD
// ─────────────────────────────────────────────────────────────────────────────
const AGENT_EMPTY = { name: "", description: "", systemPrompt: "", model: "claude-sonnet-4-6", active: true, isDefault: false };

function AgentsTab() {
  const [agents,  setAgents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [form,    setForm]    = useState(AGENT_EMPTY);
  const [saving,  setSaving]  = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.get("/whatsapp/agents");
      setAgents(res.data?.data?.agents ?? []);
    } catch { toast.error("Error cargando agentes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(AGENT_EMPTY); setModal("create"); };
  const openEdit   = (a) => { setForm({ ...a }); setModal(a); };
  const closeModal = () => { setModal(null); setForm(AGENT_EMPTY); };

  const handleSave = async () => {
    if (!form.name || !form.systemPrompt) { toast.warn("Nombre e instrucciones son requeridos"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        await API.post("/whatsapp/agents", form);
        toast.success("Agente creado");
      } else {
        await API.put(`/whatsapp/agents/${modal.id}`, form);
        toast.success("Agente actualizado");
      }
      await load();
      closeModal();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este agente? Las conversaciones que lo tenían asignado quedarán sin agente.")) return;
    try { await API.delete(`/whatsapp/agents/${id}`); await load(); toast.success("Eliminado"); }
    catch { toast.error("Error al eliminar"); }
  };

  const handleToggleActive = async (a) => {
    try { await API.put(`/whatsapp/agents/${a.id}`, { active: !a.active }); await load(); }
    catch { toast.error("Error al cambiar estado"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{agents.length} agente{agents.length !== 1 ? "s" : ""} configurado{agents.length !== 1 ? "s" : ""}</p>
        <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
          <Plus size={14} /> Nuevo agente
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Cargando…</div>
      ) : agents.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Bot size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Sin agentes. Crea uno para responder automáticamente.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {agents.map(a => (
            <div key={a.id} className={`border rounded-2xl p-4 shadow-sm ${a.active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex items-center gap-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <Bot size={15} className="text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-800 text-sm truncate">{a.name}</p>
                    <code className="text-[10px] text-gray-400">{a.model}</code>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {a.isDefault && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-supernova-100 text-supernova-700 flex items-center gap-0.5"><Sparkles size={9} /> Predeterminado</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${a.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {a.active ? "Activo" : "Inactivo"}
                  </span>
                </div>
              </div>
              {a.description && <p className="text-xs text-gray-500 mb-2">{a.description}</p>}
              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-2.5 py-1.5 mb-3 line-clamp-2">{a.systemPrompt}</p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => handleToggleActive(a)} className={`${btn} text-xs border ${a.active ? "border-gray-200 text-gray-600 hover:bg-gray-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}>
                  {a.active ? <WifiOff size={11} /> : <Wifi size={11} />} {a.active ? "Desactivar" : "Activar"}
                </button>
                <button onClick={() => openEdit(a)} className={`${btn} text-xs bg-blue-50 text-blue-600 hover:bg-blue-100`}>
                  <Pencil size={11} /> Editar
                </button>
                <button onClick={() => handleDelete(a.id)} className={`${btn} text-xs bg-red-50 text-red-500 hover:bg-red-100 ml-auto`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">{modal === "create" ? "Nuevo agente" : "Editar agente"}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                  <input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Agente de Ventas" />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Modelo</label>
                  <input className={`${inp} font-mono text-xs`} value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="claude-sonnet-4-6" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Descripción</label>
                <input className={inp} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Para qué sirve este agente" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Instrucciones (system prompt) *</label>
                <textarea
                  className={`${inp} resize-none min-h-[140px] font-mono text-xs`}
                  value={form.systemPrompt}
                  onChange={e => setForm({ ...form, systemPrompt: e.target.value })}
                  placeholder="Eres un asistente de Qkiere que ayuda a los clientes a..."
                />
              </div>
              <div className="flex items-center gap-6">
                <Toggle value={form.active} onChange={v => setForm({ ...form, active: v })} label="Agente activo" />
                <Toggle value={form.isDefault} onChange={v => setForm({ ...form, isDefault: v })} label="Usar como predeterminado" />
              </div>
              {form.isDefault && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  Este agente se asignará automáticamente a toda conversación nueva. Solo puede haber uno predeterminado.
                </p>
              )}
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
                <Save size={14} /> {saving ? "Guardando…" : "Guardar agente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Módulo principal — Multiagente WhatsApp
// ─────────────────────────────────────────────────────────────────────────────
export default function WhatsAppInbox() {
  const [tab, setTab] = useState("conversations");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await API.get("/whatsapp/conversations/stats");
      setStats(res.data?.data ?? null);
    } catch { /* silencioso — no bloquea el resto del módulo */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-100 rounded-2xl flex items-center justify-center">
          <Bot size={20} className="text-violet-600" />
        </div>
        <div>
          <h2 className="text-lg font-black text-gray-800">Multiagente WhatsApp</h2>
          <p className="text-sm text-gray-400">Bandeja de conversaciones, agentes de IA y estadísticas</p>
        </div>
      </div>

      {!stats ? (
        <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard icon={Inbox} label="Total" value={stats.total} tone="gray" />
          <StatCard icon={Clock} label="Pendientes" value={stats.pendiente} tone="amber" />
          <StatCard icon={MessageCircle} label="En atención" value={stats.enAtencion} tone="blue" />
          <StatCard icon={CheckCircle2} label="Resueltas" value={stats.resuelto} tone="green" />
          <StatCard icon={Star} label="Marcadas" value={stats.starred} tone="amber" />
          <StatCard icon={Bot} label="Responde IA" value={stats.aiHandled} tone="violet" />
          <StatCard icon={Users} label="Necesitan asesor" value={stats.needsHuman} tone="red" />
          <StatCard icon={BarChart3} label="Mensajes 24h" value={stats.messages24h} tone="gray" />
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl w-fit">
        {[["conversations", "Conversaciones", Inbox], ["agents", "Agentes IA", Bot]].map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${tab === id ? "bg-white shadow text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "conversations" && <InboxTab onChanged={loadStats} />}
      {tab === "agents" && <AgentsTab />}
    </div>
  );
}
